import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { listDeleteEquipmentHistoryFixes, listKeys, listUsers } from '../../../graphql/queries';
import { createKeys, deleteKeys, updateKeys, createDeleteEquipmentHistoryFix } from '../../../graphql/mutations';
import InventoryHeader from '../../../components/layout/InventoryHeader';
import './Key.css';
import XLSX from 'xlsx-js-style';
import { FaFileExcel } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import useFilteredData from '../../../components/layout/useFilteredDate';
import SearchBar from '../../../components/layout/SearchBar';
import { capitalize } from '../../../components/common/capitalize';
import { FaHome } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';


const Key = () => {
    const [Key, setKey] = useState([]);
    const [nextToken, setNextToken] = useState(null);
    const [showKeyAddOverlay, setShowKeyAddOverlay] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState('');
    const [showInventoryHeader, setShowInventoryHeader] = useState(false);
    const navigate = useNavigate();
    const [num_CopiesError, setNum_CopiesError] = useState('');

    const [newKey, setNewKey] = useState({
        id: '',
        key_name: '',
        key_type: '',
        location: '',
        userId: '',
        status: '',
        issue_date: '',
        returned_date: '',
        notes: '',
        num_copies: '0',
        room: '',
        tagId: '',
        shopping_store: '',
        purchase_link: '',
        team: '',

    });

    const [sortConfig, setSortConfig] = useState(null);
    const { loading1, error: userError, data: userData } = useQuery(listUsers);



    const handleEditKey = (Key) => {
        setSelectedKey(Key);
        setIsEditing(true);
        setShowKeyAddOverlay(true);
    };

    const handleSaveAsExcel = async () => {
        try {
            const { encode_range, encode_cell } = XLSX.utils;

            const KeytoExport = filteredKeys.length > 0 ? filteredKeys : Key;

            if (KeytoExport.length === 0) {
                console.error('No data to export');
                return;
            }

            const sortedKey = [...KeytoExport].sort(
                (a, b) => new Date(a.Timestamp) - new Date(b.Timestamp)
            );

            const worksheetData = sortedKey.map((Key, index) => ({
                '#': index + 1,
                'Key Name:': Key.key_name,
                'Key Type': Key.key_type,
                'Available:': Key.status,
                'Assigned to:': Key.user?.first_name
                    ? Key.user.first_name + (Key.user.last_name ? " " + Key.user.last_name : "")
                    : "N/A",
                'Location:': Key.location,
                'Team:': Key.team,
            }));

            const worksheet = XLSX.utils.json_to_sheet([]);
            XLSX.utils.sheet_add_aoa(worksheet, [['Keys']], { origin: 'A1' });
            XLSX.utils.sheet_add_json(worksheet, worksheetData, { origin: 'A2', skipHeader: false });

            const endRow = worksheetData.length + 1;
            const endCol = Object.keys(worksheetData[0]).length - 1;

            worksheet['!ref'] = encode_range({ s: { r: 0, c: 0 }, e: { r: endRow, c: endCol } });

            worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: endCol } }];

            worksheet['A1'].s = {
                font: { bold: true, sz: 20, color: { rgb: '1F4E79' } },
                alignment: { horizontal: 'center', vertical: 'center' }
            };

            for (let col = 0; col <= endCol; col++) {
                const cellAddress = encode_cell({ r: 1, c: col });
                if (!worksheet[cellAddress]) continue;

                worksheet[cellAddress].s = {
                    fill: { fgColor: { rgb: '1F4E79' } },
                    font: { bold: true, color: { rgb: 'FFFFFF' } },
                    alignment: { horizontal: 'center' },
                    border: {
                        top: { style: 'thin', color: { rgb: '000000' } },
                        left: { style: 'thin', color: { rgb: '000000' } },
                        bottom: { style: 'thin', color: { rgb: '000000' } },
                        right: { style: 'thin', color: { rgb: '000000' } },
                    }
                };
            }

            for (let row = 2; row <= endRow; row++) {
                const isEvenRow = row % 2 === 0;
                for (let col = 0; col <= endCol; col++) {
                    const cellAddress = encode_cell({ r: row, c: col });
                    if (!worksheet[cellAddress]) continue;

                    worksheet[cellAddress].s = {
                        fill: {
                            fgColor: { rgb: isEvenRow ? 'D3D3D3' : 'FFFFFF' }
                        },
                        border: {
                            top: { style: 'thin', color: { rgb: '000000' } },
                            left: { style: 'thin', color: { rgb: '000000' } },
                            bottom: { style: 'thin', color: { rgb: '000000' } },
                            right: { style: 'thin', color: { rgb: '000000' } },
                        },
                        alignment: { horizontal: 'center' }
                    };
                }
            }

            worksheet['!cols'] = [
                { wch: 5 },
                { wch: 15 },
                { wch: 10 },
                { wch: 15 },
                { wch: 30 },
                { wch: 15 },
                { wch: 15 }
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Keys');
            XLSX.writeFile(workbook, 'KeysData.xlsx');

        } catch (error) {
            console.error('Error exporting data to Excel:', error);
        }
    };


    const { loading, data, fetchMore } = useQuery(listKeys, {
        variables: { limit: 100 },
    });

    const [addKey] = useMutation(createKeys, {
        refetchQueries: [{ query: listKeys, variables: { limit: 100 } }]
    });

    const [removeKey] = useMutation(deleteKeys, {
        refetchQueries: [{ query: listKeys, variables: { limit: 100 } }]
    });
    const [updKey] = useMutation(updateKeys, {
        refetchQueries: [{ query: listKeys, variables: { limit: 100 } }]
    });

    const [addDeleteEquipmentHistory] = useMutation(createDeleteEquipmentHistoryFix, {
        refetchQueries: [{ query: listDeleteEquipmentHistoryFixes, variables: { limit: 100 } }]
    });


    useEffect(() => {
        const fetchAllKey = async () => {
            let allKeys = [];
            let currentNextToken = null;

            do {
                const { data } = await fetchMore({
                    variables: { limit: 100, nextToken: currentNextToken },
                });

                allKeys = [...allKeys, ...data.listKeys.items];
                currentNextToken = data.listKeys.nextToken;
            } while (currentNextToken);

            setKey(allKeys);
            setNextToken(currentNextToken);
        };

        if (data) {
            fetchAllKey();
        }
    }, [data, fetchMore]);


    const clearNewKey = () => {
        setNewKey({
            id: '',
            key_name: '',
            key_type: '',
            location: '',
            userId: '',
            status: '',
            issue_date: '',
            returned_date: '',
            notes: '',
            num_copies: '0',
            room: '',
            tagId: '',
            shopping_store: '',
            purchase_link: '',
            team: '',
        });
    };

    const loadMore = () => {
        if (!nextToken) return; // Check if nextToken is null

        fetchMore({
            variables: { limit: 100, nextToken },
            updateQuery: (prevResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prevResult;
                setNextToken(fetchMoreResult.listKeys.nextToken);
                return {
                    listKeys: {
                        ...fetchMoreResult.listKeys,
                        items: [...prevResult.listKeys.items, ...fetchMoreResult.listKeys.items]
                    }
                };
            }
        });
    };


    const findUserFullNameByID = (userID) => {
        if (!userID) return null;

        const userIDString = userID.toString().trim();

        if (!Array.isArray(userData?.listUserFixes?.items)) return null;
        const user = userData?.listUserFixes?.items.find((u) => u.id === userIDString);
        return user ? `${user.first_name} ${user.last_name}` : null;
    };

    // Filter 
    const filteredKeys = useFilteredData(Key || [], searchTerm, sortConfig, searchField, userData?.listUserFixes?.items);

    const handleInputChange = (e) => {
        const { name, type, checked, value } = e.target;
        let formattedValue = value;

        // Convert to lowercase 
        if (type === 'checkbox') {
            formattedValue = checked;
        }
        if (isEditing) {
            setSelectedKey({
                ...selectedKey,
                [name]: formattedValue
            });
        } else {
            setNewKey({
                ...newKey,
                id: uuidv4(),
                [name]: formattedValue
            });
        }

    };

    const handleAddKey = () => {
        // Validar si Key y newKey están definidos y son objetos válidos
        if (!newKey) {
            console.error("Key or newKey is not defined");
            return;
        }
        // Validar num_copies must be INT
        // Asegurar que num_copies nunca sea null, sino al menos 0
        const num_copiesValue = newKey.num_copies === '' || newKey.num_copies === null
            ? '0'
            : parseInt(newKey.num_copies, 10);
        // Validar que sea un número entero y mayor o igual a 0
        const isNumCopiesValid = Number.isInteger(num_copiesValue) && num_copiesValue >= 0;

        if (!isNumCopiesValid) {
            setNum_CopiesError('The Num Copies must be an integer greater than or equal to 0.');
            console.log('Num Copies validation failed');
            return;
        } else {
            setNum_CopiesError('');
        }

        // Validar y formatear las fechas correctamente
        const formattedIssue = newKey.issue_date
            ? new Date(newKey.issue_date).toISOString().split('T')[0]
            : null;

        const formattedReturn = newKey.returned_date
            ? new Date(newKey.returned_date).toISOString().split('T')[0]
            : null;


        // Validar si newKey.userId está definido y tiene un valor no vacío
        const safeUserId = newKey.userId && newKey.userId.trim() !== "" ? newKey.userId : "n/a";

        // Asegurarse de que addKey sea una función que devuelva una promesa
        if (typeof addKey !== "function") {
            console.error("addKey is not a valid function");
            return;
        }

        // Realizar la mutación con los valores formateados
        addKey({
            variables: {
                input: {
                    ...newKey,
                    userId: safeUserId,
                    issue_date: formattedIssue,
                    returned_date: formattedReturn
                }
            }
        })
            .then(() => {
                clearNewKey(); // Asegúrate de que esta función esté definida
                alert("The Key was added successfully");
                setShowKeyAddOverlay(false); // Asegúrate de que esta función esté definida
            })
            .catch(error => {
                console.error("Error adding Key:", error);
                alert("There was an error adding the key.");
            });
    };


    const handleDeleteKey = async () => {
        try {

            // Eliminar el Key
            await removeKey({
                variables: { input: { id: selectedKey.id } },
            });

            // Resetear estados
            setShowKeyAddOverlay(false);
            setIsEditing(false);
            setSelectedKey(null);

        } catch (error) {
            console.error("Error deleting Key", error);
        }
    };

    const handleUpdateKey = () => {

        const formattedData_issue = selectedKey.issue_date ? new Date(selectedKey.issue_date).toISOString().split('T')[0] : null;
        const formattedData_returned = selectedKey.returned_date ? new Date(selectedKey.returned_date).toISOString().split('T')[0] : null;
        // Validar num_copies must be INT
        const num_copiesValue = selectedKey.num_copies === '' || selectedKey.num_copies === null
            ? '0'
            : parseInt(selectedKey.num_copies, 10);
        // Validar que sea un número entero y mayor o igual a 0
        const isNumCopiesValid = Number.isInteger(num_copiesValue) && num_copiesValue >= 0;

        if (!isNumCopiesValid) {
            setNum_CopiesError('The Num Copies must be an integer greater than or equal to 0.');
            console.log('Num Copies validation failed');
            return;
        } else {
            setNum_CopiesError('');
        }


        updKey({
            variables: {
                input: {
                    id: selectedKey.id,
                    key_name: selectedKey.key_name,
                    key_type: selectedKey.key_type,
                    location: selectedKey.location,
                    userId: selectedKey.userId,
                    status: selectedKey.status,
                    issue_date: formattedData_issue,
                    returned_date: formattedData_returned,
                    notes: selectedKey.notes,
                    num_copies: selectedKey.num_copies,
                    room: selectedKey.room,
                    tagId: selectedKey.tagId,
                    shopping_store: selectedKey.shopping_store,
                    purchase_link: selectedKey.purchase_link,
                    team: selectedKey.team,
                }
            }
        }).then(() => {
            setIsEditing(true);
            setShowKeyAddOverlay(false);
            setNum_CopiesError('');


        }).catch(error => {
            console.error("Error updating Key:", error);
        });
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    //listUserFixes 
    const UsersOptions = userData?.listUserFixes?.items
        ?.filter((u) => u && u.first_name && u.last_name) // Filtrar valores nulos o sin nombres
        ?.map((u) => ({
            id: u.id,
            name: `${u.first_name.charAt(0).toUpperCase() + u.first_name.slice(1).toLowerCase()} 
           ${u.last_name.charAt(0).toUpperCase() + u.last_name.slice(1).toLowerCase()}`
        }))
        ?.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })) || [];


    const formatDate = (dateString) => {
        const options = { month: 'numeric', day: 'numeric', year: 'numeric' };
        const date = new Date(dateString);
        const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

        return localDate.toLocaleDateString(undefined, options);
    };

    return (
        <div className='key-page'>
            <header className="inventory-header">
                <h1>Key</h1>
            </header>
            <div className="searchBar">
                <button
                    onClick={() => navigate('/inventory-home')}
                >
                    <FaHome className="icon-large" /> Back to Home
                </button>
                <SearchBar searchTerm={searchTerm} onSearch={setSearchTerm} />
                <button className="add-mouse-btn" onClick={() => {
                    clearNewKey();
                    setSelectedKey(null);
                    setIsEditing(false);
                    setShowKeyAddOverlay(true);
                }}>+ Add New Key</button>
                <button onClick={handleSaveAsExcel}> <FaFileExcel /> Export </button>
                <button
                    className="toggle-header-button"
                    onClick={() => setShowInventoryHeader(!showInventoryHeader)}
                >
                    {showInventoryHeader ? "Hide Header" : "Show Header"}
                </button>
            </div>
            {/* Condición para mostrar el InventoryHeader */}
            {showInventoryHeader && (
                <>
                    <InventoryHeader />
                </>
            )}
            <div className="key-table-container">
                <table className="key-table">

                    <thead>
                        <tr>
                            <th>#</th> { }
                            <th onClick={() => requestSort('key_name')}>
                                Key Number
                                {sortConfig?.key === 'key_name' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('key_type')}>
                                Type
                                {sortConfig?.key === 'key_type' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('tagId')}>
                                TAG ID
                                {sortConfig?.key === 'tagId' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('status')}>
                                Status
                                {sortConfig?.key === 'status' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>

                            <th onClick={() => requestSort('issue_date')}>
                                Assignment Date
                                {sortConfig?.key === 'issue_date' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>

                            <th onClick={() => requestSort('userId')}>
                                Assigned To
                                {sortConfig?.key === 'userId' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>


                            <th onClick={() => requestSort('returned_date')}>
                                Returnable Date
                                {sortConfig?.key === 'returned_date' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>

                            <th onClick={() => requestSort('location')}>
                                Location
                                {sortConfig?.key === 'location' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('room')}>
                                Room
                                {sortConfig?.key === 'room' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('team')}>
                                Team
                                {sortConfig?.key === 'team' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('num_copies')}>
                                Number of copies
                                {sortConfig?.key === 'num_copies' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>

                            <th onClick={() => requestSort('shopping_store')}>
                                Shopping Store
                                {sortConfig?.key === 'shopping_store' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('purchase_link')}>
                                Purchase Link
                                {sortConfig?.key === 'purchase_link' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                            <th onClick={() => requestSort('notes')}>
                                Notes
                                {sortConfig?.key === 'notes' && (
                                    <span className={sortConfig.direction === 'ascending' ? 'sort-descending' : 'sort-ascending'} />
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredKeys.map((Key, index) => {
                            const userFullName = findUserFullNameByID(Key.userId);

                            return (

                                <tr key={Key.id} onClick={() => handleEditKey(Key)}>
                                    <td>{index + 1}</td> {/* Número de fila */}
                                    <td className="key_name">{capitalize(Key.key_name)}</td>
                                    <td className="key-type">{capitalize(Key.key_type)}</td>
                                    <td className="key-tagId">{capitalize(Key.tagId)}</td>
                                    <td className="key-status">
                                        {Key.status === 'broken' ? 'Broken' :
                                            Key.status === 'use' ? 'Use' :
                                                Key.status === 'lost' ? 'Lost' :
                                                    Key.status === 'inv' ? 'Inventario' :
                                                        ''} {/* Muestra 'Unknown' si el estado no coincide con ninguno de los anteriores */}
                                    </td>

                                    <td className="key-assignment_date">{Key.issue_date ? formatDate(Key.issue_date) : 'No Date'}</td>

                                    <td className={`userId ${searchField === 'userId' ? 'highlight' : ''}`}>
                                        {userFullName ? capitalize(userFullName) : "N/A"}
                                    </td>
                                    <td className="key-returnable_date">{Key.returned_date ? formatDate(Key.returned_date) : 'No Date'}</td>
                                    <td className="key-location">{Key.location}</td>
                                    <td className="key-room">{Key.room}</td>
                                    <td className="key-team">{Key.team}</td>
                                    <td className="key-num_copies">{Key.num_copies}</td>
                                    <td className="key-shopping_store">{Key.shopping_store}</td>
                                    <td className="key-purchase_link">{Key.purchase_link}</td>
                                    <td className="key-notes">{Key.notes}</td>

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>


            {nextToken && !loading && (
                <button onClick={loadMore}>Load More</button>
            )}

            {loading && <p>Loading...</p>}

            {showKeyAddOverlay && (

                <div className="key_add_overlay" onDoubleClick={(e) => {
                    if (e.target.classList.contains("Key_add_overlay")) {
                        setShowKeyAddOverlay(false);
                        setIsEditing(false);
                        setSelectedKey(null);
                        setNum_CopiesError('');
                    }
                }}>
                    <div className="overlay-content-inventory">
                        <div className="header-row">
                            <h2 className="header-title">{isEditing ? 'Edit Key' : 'Add New Key'}</h2>
                            <button onClick={() => {
                                setShowKeyAddOverlay(false);
                                clearNewKey();
                                setSelectedKey(null);
                                setIsEditing(false);
                                setNum_CopiesError('');
                            }}>X</button>
                        </div>

                        <label>Key Number:</label><input name="key_name" placeholder="Name" value={isEditing ? selectedKey.key_name : newKey.key_name} onChange={handleInputChange} required />
                        <label>Type:</label>
                        <select name="key_type" placeholder="Type" value={isEditing ? selectedKey.key_type : newKey.key_type} onChange={handleInputChange} >
                            <option value="" disabled hidden>Select Type</option>
                            <option value="desk">Desk</option>
                            <option value="drawer ">Drawer </option>
                            <option value="closet ">Closet </option>
                            <option value="door ">Door </option>
                            <option value="main entrance">Main Entrance</option>
                            <option value="locker ">Locker </option>
                            <option value="storage room">Storage Room</option>
                            <option value="conference room">Conference Room</option>
                        </select>
                        <label>TAG ID:</label><input name="tagId" placeholder="TAG" value={isEditing ? selectedKey.tagId : newKey.tagId} onChange={handleInputChange} />
                        <label>Status:</label>
                        <select name="status" placeholder="status" value={isEditing ? selectedKey.status : newKey.status} onChange={handleInputChange} >
                            <option value="" disabled hidden>Select Status</option>
                            <option value="use">Use</option>
                            <option value="broken">Broken</option>
                            <option value="lost">Lost</option>
                            <option value="inv">Inventario</option>
                        </select>
                        <label htmlFor="issue_date">Assignment Date:</label>
                        <input
                            type="date"
                            id="issue_date"
                            name="issue_date"
                            value={isEditing ? selectedKey.issue_date : newKey.issue_date}
                            onChange={handleInputChange}

                        />
                        <label>Assigned To:</label>
                        <select
                            name="userId"
                            value={isEditing ? selectedKey.userId : newKey.userId}
                            onChange={handleInputChange}
                        >
                            <option value="" disabled>
                                Select an employee
                            </option>
                            <option value="n/a">N/A</option>
                            {UsersOptions.length > 0 ? (
                                UsersOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))
                            ) : (
                                <option value="" disabled>No employees available</option>
                            )}


                        </select>
                        <label htmlFor="returned_date">Returnable Date:</label>
                        <input
                            type="date"
                            id="returned_date"
                            name="returned_date"
                            value={isEditing ? selectedKey.returned_date : newKey.returned_date}
                            onChange={handleInputChange}
                        />
                        <label>Location:</label><input name="location" placeholder="Location" value={isEditing ? selectedKey.location : newKey.location} onChange={handleInputChange} />
                        <label>Room:</label><input name="room" placeholder="Room" value={isEditing ? selectedKey.room : newKey.room} onChange={handleInputChange} />
                        <label>Team:</label><input name="team" placeholder="Team" value={isEditing ? selectedKey.team : newKey.team} onChange={handleInputChange} />
                        <label>Number of copies:</label><input name="num_copies" placeholder="Number" value={isEditing ? selectedKey.num_copies : newKey.num_copies} onChange={handleInputChange} />
                        <label>Shopping Store:</label><input name="shopping_store" placeholder="Store" value={isEditing ? selectedKey.shopping_store : newKey.shopping_store} onChange={handleInputChange} />
                        <label>Purchase Link:</label><input name="purchase_link" placeholder="Link" value={isEditing ? selectedKey.purchase_link : newKey.purchase_link} onChange={handleInputChange} />
                        <label>Notes:</label><input name="notes" placeholder="Notes" value={isEditing ? selectedKey.notes : newKey.notes} onChange={handleInputChange} />

                        {num_CopiesError && <p className="error">{num_CopiesError}</p>} {/* Display Num_copies error message */}

                        <div className="button-group">
                            <button className="overlay-add-key" onClick={isEditing ? handleUpdateKey : handleAddKey}>
                                {isEditing ? 'Update Key' : 'Add Key'}
                            </button>
                            {isEditing && (
                                <button
                                    className="overlay-delet-key"
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to delete this Key?')) {
                                            handleDeleteKey();
                                        }
                                    }}
                                >
                                    Delete Key
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Key;
